// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import chalk from 'chalk';
import { randomBytes } from 'crypto';
import express, { Request, Response } from 'express';
import { Server } from 'http';
import fetch from 'node-fetch';
import open from 'open';
import pkceChallenge from 'pkce-challenge';
import { readAsset } from './utils';

const OAUTH_CALLBACK_PORT = 51421;
const PORT_RANGE = 5;
const OAUTH_SCOPE = 'https://www.mochabugapis.com/auth/adapt/plugins.upload';
const AUDIENCE = 'https://adapt-dev.mochabugapis.com';

interface OAuth2Options {
  clientId: string;
  redirectUri: string;
  scope: string;
  state: string;
  authUrl: string;
  codeChallenge: string;
  audience: string;
}

interface TokenResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

interface OAuthErrorResponse {
  error: string;
  error_description?: string;
  error_uri?: string;
}

function createCodeUrl({
  clientId,
  redirectUri,
  scope,
  state,
  authUrl,
  codeChallenge,
  audience
}: OAuth2Options): string {
  const res = new URL(authUrl);
  res.searchParams.set('response_type', 'code');
  res.searchParams.set('client_id', clientId);
  res.searchParams.set('audience', audience);
  res.searchParams.set('redirect_uri', redirectUri);
  res.searchParams.set('scope', scope);
  res.searchParams.set('state', state);
  res.searchParams.set('code_challenge', codeChallenge);
  res.searchParams.set('code_challenge_method', 'S256');

  return res.toString();
}

interface CodeExchangeOptions {
  redirectUri: string;
  clientId: string;
  codeVerifier: string;
  code: string;
}

function createExchangeBody({
  redirectUri,
  codeVerifier,
  clientId,
  code
}: CodeExchangeOptions): string {
  const res = new URLSearchParams();
  res.set('grant_type', 'authorization_code');
  res.set('code_verifier', codeVerifier);
  res.set('client_id', clientId);
  res.set('code', code);
  res.set('redirect_uri', redirectUri);

  return res.toString();
}

function startServer(
  app: express.Express,
  attempt: number = 0
): Promise<{ server: Server; port: number }> {
  return new Promise((resolve, reject) => {
    // Calculate the current port to try based on the base port and the attempt number.
    const currentPort = OAUTH_CALLBACK_PORT + attempt;

    // Attempt to listen on the current port.
    const server = app.listen(currentPort, () => {
      console.log(
        chalk.green.bold(
          `Server running on http://localhost:${currentPort} to listen to OAuth2 callbacks`
        )
      );
      resolve({ server, port: currentPort });
    });

    // Handle the error event when the server fails to start.
    server.on('error', (err: any) => {
      if (err.code === 'EADDRINUSE' && attempt < PORT_RANGE - 1) {
        // If the address is in use, close the current server and try the next port.
        console.log(
          chalk.yellow.bold(
            `Port ${currentPort} is in use, trying next available port.`
          )
        );
        server.close();
        resolve(startServer(app, attempt + 1));
      } else {
        reject(err);
      }
    });
  });
}

// Define constants or import them
const LOGGING_PREFIX = '[waitForConsent]';

// Returns the access token
export async function waitForConsent(
  issuerUrl: string,
  errorUrl: string,
  clientId: string
): Promise<string | undefined> {
  console.info(
    `${LOGGING_PREFIX} Starting OAuth2 consent workflow for client: ${clientId}`
  );
  const authUrl = `${issuerUrl}/oauth2/auth`;
  const tokenUrl = `${issuerUrl}/oauth2/token`;
  console.info(`${LOGGING_PREFIX} Authorization URL: ${authUrl}`);
  console.info(`${LOGGING_PREFIX} Token URL: ${tokenUrl}`);

  const app = express();
  const state = randomBytes(32).toString('hex');

  console.info(`${LOGGING_PREFIX} Generated state for CSRF protection`);

  const localUrls = {
    base: ``,
    redirect: ``
  };

  const { code_verifier, code_challenge } = await pkceChallenge();

  console.info(`${LOGGING_PREFIX} PKCE Challenge generated`);

  let resolveToken: () => void;
  let rejectToken: () => void;
  let token: string | undefined;
  const tokenPromise = new Promise<void>((resolve, reject) => {
    resolveToken = resolve;
    rejectToken = reject;
  });

  app.get('/', async (_: Request, res: Response) => {
    const codeUrl = createCodeUrl({
      authUrl,
      clientId,
      redirectUri: localUrls.redirect,
      scope: OAUTH_SCOPE,
      state,
      codeChallenge: code_challenge,
      audience: AUDIENCE
    });
    console.info(`${LOGGING_PREFIX} Redirecting to code URL for user consent`);
    res.redirect(codeUrl);
  });

  // We need to use callbacks to the success / failed pages so that we don't
  // expose the code longer than necessary.
  app.get('/callback', async (req: Request, res: Response) => {
    if ('error' in req.query) {
      console.error(`${LOGGING_PREFIX} OAuth Error: error in callback`);
      const queryString = req.url.split('?')[1];
      res.redirect(`${errorUrl}?${queryString}`);
      rejectToken();
      return;
    }

    if (req.query.state !== state) {
      console.error(
        `${LOGGING_PREFIX} OAuth Error: Invalid state. Received state does not match.`
      );
      const errorDescription = encodeURIComponent('Invalid state parameter.');
      res.redirect(
        `${errorUrl}?error=invalid_request&error_description=${errorDescription}`
      );
      rejectToken();
      return;
    }

    console.info(`${LOGGING_PREFIX} OAuth state validated`);

    const body = createExchangeBody({
      clientId,
      redirectUri: localUrls.redirect,
      codeVerifier: code_verifier,
      code: req.query.code as string
    });

    try {
      console.info(`${LOGGING_PREFIX} Exchanging code for token`);
      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json'
        },
        body
      });

      // Redirect to error
      if (!response.ok) {
        console.error(
          `${LOGGING_PREFIX} OAuth Error: could not exchange code for token`
        );
        const queryString = response.url.split('?')[1];
        res.redirect(`${errorUrl}?${queryString}`);
        rejectToken();
        return;
      }

      const data = (await response.json()) as TokenResponse;
      console.info(`${LOGGING_PREFIX} Token exchange successful`);
      console.info(`${LOGGING_PREFIX} Token exchange successful. Details: `, {
        expires_in: data.expires_in,
        scope: data.scope,
        type: data.token_type,
        token: '<secret>'
      });
      token = data.access_token;
      res.redirect('/success');
    } catch (error) {
      console.error(`${LOGGING_PREFIX} Error exchanging token: ${error}`);
      res.redirect(
        `${errorUrl}?error=unkown&error_description=${encodeURIComponent(
          'Unknown error during token exchange'
        )}`
      );
      rejectToken();
    }
  });

  app.get('/success', (_: Request, res: Response) => {
    res.send(readAsset('success_page.html'));
    if (!token) {
      console.error('Token is undefined');
      rejectToken();
    }
    resolveToken();
  });

  let server: Server;
  try {
    console.info(`${LOGGING_PREFIX} Starting local server to handle callbacks`);
    const serverStart = await startServer(app);
    server = serverStart.server;
    localUrls.base = `http://localhost:${serverStart.port}`;
    localUrls.redirect = `${localUrls.base}/callback`;
    console.info(`${LOGGING_PREFIX} Server started on ${localUrls.base}`);
  } catch (e) {
    console.error(`${LOGGING_PREFIX} Error starting server: ${e}`);
    return;
  }

  try {
    console.info(`${LOGGING_PREFIX} Opening user consent page`);
    await open(localUrls.base);
    console.info(`${LOGGING_PREFIX} Waiting for user consent...`);
    await tokenPromise;
    console.info(
      `${LOGGING_PREFIX} OAuth consent workflow completed successfully`
    );
    if (!token) {
      console.error('Token is undefined');
    }
    return token;
  } catch (error) {
    console.error(`${LOGGING_PREFIX} Error in OAuth2 flow: ${error}`);
    return undefined;
  } finally {
    console.info(`${LOGGING_PREFIX} Closing the server...`);
    server.closeAllConnections();
    server.close();
    console.info(`${LOGGING_PREFIX} Server closed`);
  }
}
